import React from "react";
import {
  Badge,
  Box,
  Button,
  Divider,
  Heading,
  HStack,
  Spinner,
  Text,
  VStack,
  useToast,
} from "@chakra-ui/react";
import { CheckCircle2, Clock3, ShieldCheck, UserRoundCheck } from "lucide-react";
import { useNavigate } from "react-router-dom";
import OnboardingLayout from "../components/onboarding/OnboardingLayout";

export default function OnboardingTurnOn() {
  const navigate = useNavigate();
  const [syncData, setSyncData] = React.useState(null);
  const [loading, setLoading] = React.useState(true);

  React.useEffect(() => {
    markStepViewed();
    loadPageData();
  }, []);

  const markStepViewed = async () => {
    try {
      await fetch("/api/onboarding_progress", {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": document.querySelector('[name="csrf-token"]').content,
        },
        body: JSON.stringify({ step_id: "turn-on" }),
      });
    } catch (error) {
      // Non-blocking: onboarding page should remain usable.
    } finally {
      window.dispatchEvent(new Event("onboarding-progress-refresh"));
    }
  };

  const loadPageData = async () => {
    try {
      const dataStatusResponse = await fetch("/api/data_status");
      const dataStatusPayload = await dataStatusResponse.json().catch(() => null);

      if (dataStatusResponse.ok && dataStatusPayload?.success) {
        setSyncData(dataStatusPayload.data);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleFinishOnboarding = async () => {
    try {
      await fetch("/api/onboarding_progress", {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": document.querySelector('[name=\"csrf-token\"]').content,
        },
        body: JSON.stringify({ step_id: "turn-on" }),
      });
      window.dispatchEvent(new Event("onboarding-progress-refresh"));
    } catch (error) {
      // Continue navigation even if completion marker fails
    } finally {
      navigate("/");
    }
  };

  const isSyncing = syncData?.sync_status === "in_progress";
  const currentPhase = syncData?.sync_state_breakdown?.current_phase;
  const connectedProvider = syncData?.connected_provider;
  const providerLabel = syncData?.provider_label || (connectedProvider === "service_m8" ? "ServiceM8" : "Jobber");
  const isServiceM8 = connectedProvider === "service_m8";
  const teamOptInInstruction = isServiceM8
    ? "Only add the customer to the Same Day Openings waitlist after the customer says yes."
    : "Only mark the Jobber waitlist custom field as opted in when the customer says yes.";
  const signalInstruction = isServiceM8
    ? "We use the Same Day Openings waitlist setting as the signal that this customer asked to hear about earlier openings."
    : "We use the Jobber waitlist custom field as the signal that this customer asked to hear about earlier openings.";

  return (
    <OnboardingLayout
      title="How waitlist consent works"
      subtitle={`Before you go live, make sure your team knows what they need to do with ${providerLabel} and what Same Day Openings handles automatically.`}
    >
      <VStack align="stretch" spacing={8}>
        <Box border="1px solid" borderColor="gray.200" borderRadius="lg" p={6}>
          <Heading size="sm" mb={4}>What your team is responsible for</Heading>
          <VStack align="stretch" spacing={4}>
            <HStack align="start" spacing={3}>
              <UserRoundCheck size={18} color="var(--chakra-colors-blue-500)" />
              <Text color="gray.700">
                Ask on the booking call: “Would you like to join our waitlist in case an earlier slot opens up?”
              </Text>
            </HStack>
            <HStack align="start" spacing={3}>
              <UserRoundCheck size={18} color="var(--chakra-colors-blue-500)" />
              <Text color="gray.700">
                {teamOptInInstruction}
              </Text>
            </HStack>
            <HStack align="start" spacing={3}>
              <UserRoundCheck size={18} color="var(--chakra-colors-blue-500)" />
              <Text color="gray.700">
                Keep using that verbal opt-in process for every new appointment your team wants on the waitlist.
              </Text>
            </HStack>
          </VStack>
        </Box>

        <Box border="1px solid" borderColor="gray.200" borderRadius="lg" p={6}>
          <Heading size="sm" mb={4}>What Same Day Openings is doing</Heading>
          <VStack align="stretch" spacing={4}>
            <HStack align="start" spacing={3}>
              <ShieldCheck size={18} color="var(--chakra-colors-green-500)" />
              <Text color="gray.700">
                {signalInstruction}
              </Text>
            </HStack>
            <HStack align="start" spacing={3}>
              <ShieldCheck size={18} color="var(--chakra-colors-green-500)" />
              <Text color="gray.700">
                We send the customer a direct confirmation text from your waitlist number so they know what to expect and have a clear opt-out path.
              </Text>
            </HStack>
            <HStack align="start" spacing={3}>
              <ShieldCheck size={18} color="var(--chakra-colors-green-500)" />
              <Text color="gray.700">
                We only text customers who were individually added to the waitlist flow. We are not treating your future appointments as a mass marketing list.
              </Text>
            </HStack>
          </VStack>
        </Box>

        <Box border="1px solid" borderColor="blue.200" bg="blue.50" borderRadius="lg" p={6}>
          <HStack justify="space-between" align="start" spacing={4} mb={4} flexWrap="wrap">
            <Box>
              <Badge colorScheme="blue" mb={3}>{providerLabel} sync status</Badge>
              <Heading size="sm" mb={2}>We’re syncing your {providerLabel} data in the background.</Heading>
              <Text color="blue.800" fontSize="sm">
                This usually takes about 10 minutes. Once the sync is done, your dashboard has the {providerLabel} data it needs for waitlist automation and reporting.
              </Text>
            </Box>
            {loading ? (
              <Spinner color="blue.500" />
            ) : isSyncing ? (
              <HStack color="blue.700">
                <Spinner size="sm" />
                <Text fontSize="sm" fontWeight="semibold">Syncing now</Text>
              </HStack>
            ) : (
              <HStack color="green.700">
                <CheckCircle2 size={18} />
                <Text fontSize="sm" fontWeight="semibold">Sync status available</Text>
              </HStack>
            )}
          </HStack>

          <Box
            bg="white"
            border="1px solid"
            borderColor="blue.100"
            borderRadius="md"
            p={4}
          >
            <HStack align="start" spacing={3}>
              <Clock3 size={18} color="var(--chakra-colors-blue-500)" />
              <Box>
                <Text fontWeight="semibold" color="gray.900">
                  {isSyncing ? (currentPhase || `${providerLabel} sync is in progress.`) : `${providerLabel} sync is running in the background and we’ll keep this preference on file.`}
                </Text>
              </Box>
            </HStack>
          </Box>
        </Box>

        <HStack justify="space-between">
          <Button variant="outline" onClick={() => navigate("/onboarding/availability")}>
            Back
          </Button>
          <Button colorScheme="blue" onClick={handleFinishOnboarding}>
            Finish Onboarding
          </Button>
        </HStack>
      </VStack>
    </OnboardingLayout>
  );
}
